import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hideNativeSplash, restoreAppStatusBar } from './native/initNativeShell';

const NAVY = '#0A1628';
const CYAN = '#55FFF4';
const WHITE = '#F1F5F9';
const MUTED = 'rgba(241, 245, 249, 0.65)';

const LOGO_SIZE = 148;
const RING_SIZE = 188;
const RING_STROKE = 2.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;

interface AnimatedSplashProps {
  isReady: boolean;
  onFinish: () => void;
}

function useWindowSize() {
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  useEffect(() => {
    const onResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return size;
}

function WaveMesh({ width }: { width: number }) {
  const dots = useMemo(
    () =>
      Array.from({ length: 48 }, (_, index) => ({
        x: (index / 47) * width,
        y: 28 + Math.sin(index * 0.45) * 14 + Math.sin(index * 0.18) * 8,
        r: index % 3 === 0 ? 2.2 : 1.4,
        opacity: 0.35 + (index % 5) * 0.08,
      })),
    [width],
  );

  return (
    <svg className="splash-wave" width={width} height={64} aria-hidden="true">
      {dots.map((dot, index) => (
        <circle
          key={index}
          cx={dot.x}
          cy={dot.y}
          r={dot.r}
          fill={CYAN}
          opacity={dot.opacity}
        />
      ))}
    </svg>
  );
}

export const AnimatedSplash: React.FC<AnimatedSplashProps> = ({ isReady, onFinish }) => {
  const { width, height } = useWindowSize();
  const [entranceComplete, setEntranceComplete] = useState(false);
  const [exiting, setExiting] = useState(false);
  const exitStarted = useRef(false);
  const finished = useRef(false);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    void restoreAppStatusBar().finally(() => {
      onFinishRef.current();
    });
  }, []);

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor;
    document.body.style.backgroundColor = NAVY;

    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        void hideNativeSplash();
      });
    });

    return () => {
      document.body.style.backgroundColor = previousBackground;
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setEntranceComplete(true), 2200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady || !entranceComplete || exitStarted.current) return;
    exitStarted.current = true;
    setExiting(true);
  }, [entranceComplete, isReady]);

  useEffect(() => {
    if (!exiting) return;
    const timer = window.setTimeout(finish, 400);
    return () => window.clearTimeout(timer);
  }, [exiting, finish]);

  const glowSize = Math.min(width, height) * 0.72;

  const handleLoadingAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== 'splash-fade-in') return;
    setEntranceComplete(true);
  };

  const handleExitAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== 'splash-exit') return;
    finish();
  };

  return (
    <div
      className={`splash-root fixed inset-0 z-50 flex items-center justify-center ${exiting ? 'is-exiting' : ''}`}
      style={{ backgroundColor: NAVY }}
      role="status"
      aria-label="Passport loading screen"
      onAnimationEnd={handleExitAnimationEnd}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <div className="splash-glow" style={{ width: glowSize, height: glowSize }}>
          <svg width={glowSize} height={glowSize}>
            <defs>
              <radialGradient id="splash-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={CYAN} stopOpacity="0.28" />
                <stop offset="45%" stopColor={CYAN} stopOpacity="0.1" />
                <stop offset="100%" stopColor={CYAN} stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx={glowSize / 2} cy={glowSize / 2} r={glowSize / 2} fill="url(#splash-glow)" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center px-8">
        <div
          className="splash-logo-wrap relative flex items-center justify-center"
          style={{ width: RING_SIZE, height: RING_SIZE }}
        >
          <div className="splash-ring absolute inset-0 flex items-center justify-center">
            <svg width={RING_SIZE} height={RING_SIZE} aria-hidden="true">
              <circle
                className="splash-ring-stroke"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                stroke={CYAN}
                strokeWidth={RING_STROKE}
                fill="none"
                strokeLinecap="round"
                opacity={0.9}
              />
            </svg>
          </div>
          <img
            src="/icon-512x512.png"
            alt=""
            width={LOGO_SIZE}
            height={LOGO_SIZE}
            className="relative"
            style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
          />
        </div>

        <div className="splash-brand mt-7 flex flex-col items-center gap-2">
          <p className="text-[28px] font-bold" style={{ color: WHITE, letterSpacing: '4px' }}>
            PASSPORT
          </p>
          <p className="text-sm font-medium" style={{ color: MUTED, letterSpacing: '0.4px' }}>
            Your professional identity, verified.
          </p>
        </div>

        <div
          className="splash-loading mt-9 flex flex-col items-center gap-3"
          onAnimationEnd={handleLoadingAnimationEnd}
        >
          <div className="splash-spinner" aria-hidden="true" />
          <p className="text-[13px] font-medium" style={{ color: MUTED }}>
            Preparing your experience...
          </p>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-12 left-0" aria-hidden="true">
        <WaveMesh width={width} />
      </div>
    </div>
  );
};
